import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, PackageSearch } from "lucide-react";
import logo from "@/assets/logo.png";
import { scrollToSection, scrollToTop } from "@/lib/lenis";
import { TrackShipmentDialog } from "@/components/TrackShipmentDialog";

const NAV_LINKS = [
  { label: "Services", href: "#services", type: "scroll" },
  { label: "Trade Lanes", href: "#trade-lanes", type: "scroll" },
  { label: "Customs Filing", href: "#customs-filing", type: "scroll" },
  { label: "About", href: "#about", type: "scroll" },
  { label: "Resources", href: "/resources", type: "route" },
  { label: "Contact", href: "#contact", type: "scroll" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [trackOpen, setTrackOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollTo = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(href), 200);
    } else {
      scrollToSection(href);
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      scrollToTop();
    }
  };

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 inset-x-0 z-50 bg-[#0A140F]/85 backdrop-blur-md border-b border-gold/15"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-10 h-16 sm:h-20 flex items-center justify-between">
        <a
          href="/"
          data-testid="navbar-logo-link"
          onClick={handleLogoClick}
          className="flex items-center gap-3"
        >
          <img
            src={logo}
            alt="Ferguson's Customs Broker & Freight Forwarders"
            className="h-12 sm:h-14 w-auto logo-glow"
          />
          <span className="hidden sm:block font-display font-bold text-sm sm:text-base leading-tight text-gold-light">
            FERGUSON'S
            <span className="block text-[10px] font-body font-normal tracking-wide text-text-secondary text-[#A1B0A6]">
              CUSTOMS BROKER &amp; FREIGHT FORWARDERS
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) =>
            link.type === "route" ? (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`navbar-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setOpen(false)}
                className="text-sm font-medium text-[#A1B0A6] hover:text-gold transition-colors"
              >
                {link.label}
              </Link>
            ) : (
              <button
                key={link.href}
                data-testid={`navbar-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => scrollTo(link.href)}
                className="text-sm font-medium text-[#A1B0A6] hover:text-gold transition-colors"
              >
                {link.label}
              </button>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <a
            href="tel:+18763958332"
            data-testid="navbar-phone-link"
            className="flex items-center gap-2 text-sm text-[#A1B0A6] hover:text-gold transition-colors"
          >
            <Phone size={15} />
            876-395-8332
          </a>
          <button
            data-testid="navbar-track-shipment-btn"
            onClick={() => setTrackOpen(true)}
            className="flex items-center gap-1.5 text-sm font-medium text-[#A1B0A6] hover:text-gold border border-gold/30 hover:border-gold/60 rounded-full px-4 py-2 transition-colors"
          >
            <PackageSearch size={15} />
            Track Shipment
          </button>
          <button
            data-testid="navbar-request-quote-btn"
            onClick={() => scrollTo("#quote")}
            className="bg-gold hover:bg-gold-hover text-[#0A140F] font-display font-semibold text-sm px-5 py-2.5 rounded-full transition-colors"
          >
            Request a Quote
          </button>
        </div>

        <button
          data-testid="navbar-mobile-menu-toggle"
          className="lg:hidden text-gold"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden border-t border-gold/15 bg-[#0A140F]"
            data-testid="navbar-mobile-menu"
          >
            <div className="flex flex-col px-6 py-4 gap-4">
              {NAV_LINKS.map((link) =>
                link.type === "route" ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-navbar-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="text-left text-base font-medium text-[#A1B0A6] hover:text-gold py-1"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <button
                    key={link.href}
                    data-testid={`mobile-navbar-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => scrollTo(link.href)}
                    className="text-left text-base font-medium text-[#A1B0A6] hover:text-gold py-1"
                  >
                    {link.label}
                  </button>
                )
              )}
              <button
                data-testid="mobile-navbar-request-quote-btn"
                onClick={() => scrollTo("#quote")}
                className="bg-gold text-[#0A140F] font-display font-semibold text-sm px-5 py-3 rounded-full mt-2"
              >
                Request a Quote
              </button>
              <button
                data-testid="mobile-navbar-track-shipment-btn"
                onClick={() => {
                  setOpen(false);
                  setTrackOpen(true);
                }}
                className="flex items-center justify-center gap-1.5 border border-gold/30 text-[#A1B0A6] font-display font-semibold text-sm px-5 py-3 rounded-full"
              >
                <PackageSearch size={15} />
                Track Shipment
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <TrackShipmentDialog open={trackOpen} onOpenChange={setTrackOpen} />
    </header>
  );
};
